import Vue from "vue";
import Vuex from "vuex";

import ApiService from "ceres/app/services/ApiService";

import address from "./modules/AddressModule";
import basket from "~/app/store/modules/BasketModule";
import checkout from "ceres/app/store/modules/CheckoutModule";
import consents from "ceres/app/store/modules/ConsentModule";
import contactForm from "./modules/ContactFormModule";
import itemList from "./modules/ItemListModule";
import itemSearch from "ceres/app/store/modules/ItemSearchModule";
import lastSeen from "ceres/app/store/modules/LastSeenModule";
import lazyComponent from "ceres/app/store/modules/LazyComponentModule";
import liveShopping from "ceres/app/store/modules/LiveShoppingModule";
import localization from "ceres/app/store/modules/LocalizationModule";
import customLocalization from "~/app/store/modules/CustomLocalizationModule";
import navigation from "ceres/app/store/modules/NavigationModule";
import orderReturn from "ceres/app/store/modules/OrderReturnModule";
import user from "ceres/app/store/modules/UserModule";
import wishList from "ceres/app/store/modules/WishListModule";
import viewPort from "./modules/ViewPortModule";
import items from "./modules/singleItem/BaseItemModule";

import eventPropagation from "ceres/app/store/plugins/EventPropagationPlugin";
import { getUrlParams } from "ceres/app/services/UrlService";
import { isDefined } from "ceres/app/helper/utils";


// =========================
// init vuex store
// =========================
export let store;

// TODO: add code comment
export function createStore() {
    Vue.options.delimiters = ["${", "}"];
    Vue.use(Vuex);

    // eslint-disable-next-line
    store = new Vuex.Store(
        {
            modules:
                {
                    address,
                    basket,
                    checkout,
                    consents,
                    contactForm,
                    itemList,
                    items,
                    itemSearch,
                    lastSeen,
                    lazyComponent,
                    liveShopping,
                    localization,
                    customLocalization,
                    navigation,
                    orderReturn,
                    user,
                    wishList,
                    viewPort
                },

            plugins: !App.isSSR ? [eventPropagation] : []
        });

    return store;
}

// =========================
// Fill initial vuex data
// =========================

export function initServerStore(store) {
    store.commit("setShippingCountries", App.initialData.shippingCountries);
    store.commit("setShippingCountryId", App.initialData.shippingCountryId);
    store.commit("setShowNetPrices", App.initialData.showNetPrices);

    if (App.initialData.euShippingCountries) {
        store.commit("setEuShippingCountries", App.initialData.euShippingCountries);
    }
}

export function initClientListeners(store) {
    ApiService.listen("LocalizationChanged",
        data => {
            store.commit("setShippingCountries", data.localization.activeShippingCountries);
            store.commit("setShippingCountryId", data.localization.currentShippingCountryId);
        });

    ApiService.listen("AfterBasketChanged",
        data => {
            store.commit("setBasket", data.basket);
            store.commit("setShowNetPrices", data.showNetPrices);
            store.commit("updateBasketItems", data.basketItems);
            store.commit("setWishListIds", data.basket.itemWishListIds);
        });

    store.dispatch("loadBasketData");

    ApiService.listen("AfterBasketItemAdd", data => {
        store.commit("addBasketItem", data.basketItems);
    });

    ApiService.listen("AfterBasketItemUpdate", data => {
        store.commit("updateBasketItem", data.basketItems[0]);
    });

    ApiService.after(() => {
        // unset flag that indicates a basket item quantity update after each request.
        store.commit("setIsBasketItemQuantityUpdate", false);
    });
    /**
     * Loads user data after pageload
     */
    ApiService.get("/rest/io/customer", {}, { keepOriginalResponse: true })
        .done(response => {
            if (isDefined(response.data)) {
                store.commit("setUserData", response.data);
            }
        });

    /**
     * Adds login/logout event listeners
     */
    ApiService.listen("AfterAccountAuthentication", userData => {
        store.commit("setUserData", userData.accountContact);
    });
    ApiService.listen("AfterAccountContactLogout", () => {
        store.commit("setUserData", null);
    });
}

export function initClientStore(store) {
    store.commit("initConsents");

    if (App.isSearch) {
        store.dispatch("loadComponent", "item-search");
    }

    // Use DOMContentLoaded to load session data after app has been initialized
    document.addEventListener("DOMContentLoaded", () => {
        const urlParams = getUrlParams();

        if (store.getters.currentItemVariation) {
            urlParams.lastSeenVariationId = store.getters.currentItemVariation.variation.id;
        }

        ApiService.get("/rest/io/session", urlParams, { cache: false, keepOriginalResponse: true })
            .done(response => {
                if (isDefined(response.data.customer)) {
                    store.commit("setUserData", response.data.customer);
                }

                if (!response.events.hasOwnProperty("AfterBasketChanged")) {
                    // only set basket if not change event is emitted. In this case, the basket will be set by the event listener.
                    store.commit("setBasket", response.data.basket);
                    store.commit("setWishListIds", response.data.basket.itemWishListIds);
                }

                store.commit("setIsBasketInitiallyLoaded");
                store.commit("setBasketItems", response.data.basketItems);
            })
            .catch((error, status) => {
                console.log(error, status);

                if (status > 0) {
                    NotificationService.error(
                        TranslationService.translate("Ceres::Template.basketOops")
                    ).closeAfter(10000);
                }
            });
    });
}

export default { createStore, initServerStore, initClientListeners, initClientStore, store };
